import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAgents } from "@/components/Agents/AgentsContext";
import { apiGet, apiPatch, apiPost } from "@/lib/apiClient";
import {
  isBookCollectionKey,
  isKbMembershipKey,
  knowledgeBaseLabel,
  stripKbMembershipKeys,
  withKbMembership,
} from "@/lib/knowledgeBaseRegistry";
import {
  articlePath,
  type KnowledgeArticle,
  type KnowledgeArticleStatus,
} from "@/lib/knowledgeModel";
import { agentsHref } from "@/lib/atwAgentsHost";
import {
  KnowledgeChrome,
  KnowledgeError,
  KnowledgeNeedsBase,
  knowledgeSecondaryLinkClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { UploadMarkdownArticleButton } from "@/pages/knowledge/UploadMarkdownArticleButton";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";
import { useKnowledgeRouteBase } from "@/pages/knowledge/useKnowledgeRouteBase";

const fieldClass =
  "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-white/20 dark:bg-zinc-900 dark:text-white dark:placeholder:text-zinc-500";

export function ArticlePage({ mode }: { mode: "create" | "edit" }) {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { orgContextStatus, orgContextError } = useAgents();
  const { books: customBooks } = useCustomBooks();
  const { activeId, active, hasBases } = useKnowledgeBases();
  const baseId = useKnowledgeRouteBase() || activeId;
  const books = useMemo(
    () => customBooks.map((book) => ({ collectionKey: book.collectionKey, title: book.title })),
    [customBooks],
  );
  const knownBookKeys = useMemo(() => new Set(books.map((book) => book.collectionKey)), [books]);

  const [title, setTitle] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [status, setStatus] = useState<KnowledgeArticleStatus>("draft");
  const [collectionKeys, setCollectionKeys] = useState<string[]>([]);
  const [storedKbKeys, setStoredKbKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (orgContextStatus === "missing") {
      setError(orgContextError || "Organization context required.");
      setLoading(false);
      return;
    }
    if (mode !== "edit") {
      if (orgContextStatus === "ready") setLoading(false);
      return;
    }
    if (orgContextStatus !== "ready") {
      setLoading(true);
      return;
    }
    if (!articleId) {
      setError("Article id is missing.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      const res = await apiGet<KnowledgeArticle & { error?: string }>(
        `/api/knowledge/articles/${encodeURIComponent(articleId)}`,
      );
      if (cancelled) return;
      if (!res.ok || !res.data?.id) {
        setError(res.error || "Could not load article.");
        setLoading(false);
        return;
      }
      setTitle(res.data.title || "");
      setMarkdown(typeof res.data.markdown === "string" ? res.data.markdown : "");
      setStatus(res.data.status === "published" ? "published" : "draft");
      {
        const keys = Array.isArray(res.data.collectionKeys) ? res.data.collectionKeys : [];
        setStoredKbKeys(keys.filter(isKbMembershipKey));
        setCollectionKeys(keys.filter(isBookCollectionKey));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, articleId, orgContextStatus, orgContextError]);

  function toggleKey(key: string) {
    if (isKbMembershipKey(key)) return;
    setCollectionKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key].sort(),
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    if (orgContextStatus !== "ready") {
      setError(orgContextError || "Organization context required.");
      return;
    }
    if (mode === "create" && !activeId.trim()) {
      setError("Select or create a knowledge base first.");
      return;
    }
    setSaving(true);
    setError(null);
    const nextKeys =
      mode === "create"
        ? withKbMembership(collectionKeys, activeId)
        : Array.from(new Set([...stripKbMembershipKeys(collectionKeys), ...storedKbKeys])).sort();
    const body = { title: title.trim(), markdown, status, collectionKeys: nextKeys };
    const res =
      mode === "create"
        ? await apiPost<KnowledgeArticle>("/api/knowledge/articles", body)
        : await apiPatch<KnowledgeArticle>(
            `/api/knowledge/articles/${encodeURIComponent(articleId || "")}`,
            body,
          );
    setSaving(false);
    if (!res.ok || !res.data?.id) {
      setError(res.error || "Could not save article.");
      return;
    }
    void navigate(agentsHref(articlePath(baseId, res.data.id)), { replace: true });
  }

  const extraKeys = collectionKeys.filter((key) => !knownBookKeys.has(key) && isBookCollectionKey(key));
  const cancelHref =
    mode === "edit" && articleId
      ? agentsHref(articlePath(baseId, articleId))
      : agentsHref("/knowledge/articles");
  const baseName = active ? knowledgeBaseLabel(active) : "this knowledge base";

  if (mode === "create" && !hasBases) {
    return (
      <KnowledgeChrome
        title="New article"
        description="Articles live inside a knowledge base. Create a knowledge base first."
        badge="Markdown"
        showBaseSwitcher={false}
      >
        <KnowledgeNeedsBase />
      </KnowledgeChrome>
    );
  }

  return (
    <KnowledgeChrome
      title={mode === "create" ? "New article" : "Edit article"}
      description={
        mode === "create"
          ? `Write markdown for "${baseName}", or upload a .md file. Books are optional. Saves go to the shared knowledge store used by One.`
          : `Write markdown for "${baseName}". Books are optional. Saves go to the shared knowledge store used by One.`
      }
      badge="Markdown"
      actions={
        <>
          {mode === "create" ? <UploadMarkdownArticleButton /> : null}
          <Link to={cancelHref} className={knowledgeSecondaryLinkClass}>
            {mode === "edit" ? "Cancel" : "Articles"}
          </Link>
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {loading ? <p className="text-sm text-slate-500 dark:text-zinc-500">Loading article…</p> : null}
      {!loading ? (
        <form
          onSubmit={(event) => void onSubmit(event)}
          className="max-w-3xl space-y-4 rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-zinc-950/40 p-6"
        >
          <label className="block text-sm">
            <span className="font-medium text-zinc-300">Title</span>
            <input
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Article title"
              className={fieldClass}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-zinc-300">Status</span>
            <select
              value={status}
              onChange={(event) =>
                setStatus(event.target.value === "published" ? "published" : "draft")
              }
              className={fieldClass}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <span className="mt-1 block text-xs text-slate-500 dark:text-zinc-500">
              Published articles are ready for assignment on an agent later. Drafts stay in
              the knowledge base for editing.
            </span>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-zinc-300">Markdown</span>
            <textarea
              value={markdown}
              onChange={(event) => setMarkdown(event.target.value)}
              rows={18}
              placeholder={"# Heading\n\nWrite the article in markdown."}
              className={`${fieldClass} font-mono`}
            />
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700 dark:text-zinc-300">Books (optional)</legend>
            <p className="text-xs text-slate-500 dark:text-zinc-500">
              Turn on each book this article belongs to, or leave none selected. A library is not
              required. Non-Primary bases also store a kb membership key on save.
            </p>
            {books.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-zinc-500">
                No books are available yet. Create a custom book from Books, or save a standalone
                article.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {books.map((book) => {
                  const selected = collectionKeys.includes(book.collectionKey);
                  return (
                    <button
                      key={book.collectionKey}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => toggleKey(book.collectionKey)}
                      className={
                        selected
                          ? "rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white"
                          : "rounded-md border border-white/20 px-3 py-1.5 text-sm font-medium text-slate-800 dark:text-zinc-200"
                      }
                    >
                      {book.title}
                    </button>
                  );
                })}
              </div>
            )}
            {collectionKeys.length === 0 ? (
              <p className="text-xs text-indigo-300/90">
                No books selected. This article will be standalone in "{baseName}".
              </p>
            ) : null}
            {extraKeys.length > 0 ? (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-slate-500 dark:text-zinc-500">
                  Other collection keys already stored on this article. Leave them on to keep the
                  same membership.
                </p>
                <div className="flex flex-wrap gap-2">
                  {extraKeys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed
                      onClick={() => toggleKey(key)}
                      className="rounded-md bg-zinc-700 px-3 py-1.5 font-mono text-xs font-medium text-white"
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </fieldset>
          <button
            type="submit"
            disabled={saving || !title.trim()}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {saving ? "Saving…" : mode === "create" ? "Create article" : "Save changes"}
          </button>
        </form>
      ) : null}
    </KnowledgeChrome>
  );
}
